import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, Alert,
  CircularProgress, IconButton, InputAdornment,
  Divider, LinearProgress, Chip,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PedalBikeIcon from '@mui/icons-material/PedalBike';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SecurityIcon from '@mui/icons-material/Security';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckIcon from '@mui/icons-material/Check';
import WavingHandIcon from '@mui/icons-material/WavingHand';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/high-res.css';

/* ── Design tokens ── */
const T = {
  purple: '#552583',
  purpleDark: '#3a1760',
  gold: '#FDB927',
  bg: '#E4F0F6',
  card: '#ffffff',
  text: '#1a1a1a',
  muted: 'rgba(0,0,0,0.45)',
  inputBg: 'rgba(85,37,131,0.03)',
  inputBorder: 'rgba(85,37,131,0.14)',
  inputFocus: 'rgba(85,37,131,0.45)',
  radius: { input: '10px', btn: '12px', card: '24px', alert: '12px' },
};

const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    <path fill="none" d="M0 0h48v48H0z"/>
  </svg>
);

const STEPS = ['Identité', 'Coordonnées', 'Sécurité', 'Vérification'];

const PARTNER_TYPES = [
  { IconComp: DirectionsCarIcon, label: 'Chauffeur VTC' },
  { IconComp: PedalBikeIcon, label: 'Livreur' },
  { IconComp: RestaurantIcon, label: 'Restaurateur' },
];

const PERKS = [
  { IconComp: ShoppingBagIcon, title: 'Commandez en 2 clics', desc: 'Restaurants, courses, livraisons depuis votre téléphone' },
  { IconComp: LocationOnIcon, title: 'Suivi en temps réel', desc: 'Suivez votre livreur sur la carte' },
  { IconComp: SecurityIcon, title: 'Paiement sécurisé', desc: 'Carte bancaire ou virement, toujours protégé' },
  { IconComp: BoltIcon, title: 'Livraison express', desc: "Moins de 30 min en moyenne sur l'île" },
];

const inputSx = {
  '& .MuiOutlinedInput-root': {
    background: T.inputBg,
    borderRadius: T.radius.input,
    transition: 'border-color 0.2s',
    '& fieldset': { borderColor: T.inputBorder },
    '&:hover fieldset': { borderColor: 'rgba(85,37,131,0.3)' },
    '&.Mui-focused fieldset': { borderColor: T.inputFocus, borderWidth: '1.5px' },
  },
  '& input': { color: T.text, py: 1.5, fontSize: '0.95rem', '&::placeholder': { color: 'rgba(0,0,0,0.25)' } },
};

const labelSx = {
  color: T.muted,
  fontSize: '0.68rem', fontWeight: 700,
  letterSpacing: '0.06em', textTransform: 'uppercase',
  mb: 0.8, display: 'block',
};

const btnPrimary = {
  py: 1.6, borderRadius: T.radius.btn, textTransform: 'none',
  fontWeight: 700, fontSize: '0.95rem',
  background: T.purple, color: 'white',
  boxShadow: '0 4px 16px rgba(85,37,131,0.25)',
  transition: 'all 0.15s ease',
  '&:hover': { background: T.purpleDark, boxShadow: '0 6px 24px rgba(85,37,131,0.35)' },
  '&:active': { boxShadow: '0 2px 8px rgba(85,37,131,0.2)' },
  '&:disabled': { background: 'rgba(85,37,131,0.12)', color: 'rgba(0,0,0,0.25)', boxShadow: 'none' },
};

const btnSecondary = {
  py: 1.6, px: 2.5, borderRadius: T.radius.btn, textTransform: 'none',
  fontWeight: 600, flexShrink: 0,
  borderColor: 'rgba(85,37,131,0.18)', color: 'rgba(85,37,131,0.65)',
  '&:hover': { borderColor: 'rgba(85,37,131,0.35)', background: 'rgba(85,37,131,0.04)' },
};

const phoneBoxSx = {
  '& .react-tel-input': { width: '100%' },
  '& .react-tel-input .flag.re': { backgroundPosition: '-192px -120px' },
  '& .react-tel-input .flag.yt': { backgroundPosition: '-192px -120px' },
  '& .react-tel-input .form-control': {
    width: '100%', height: '52px',
    background: T.inputBg,
    border: `1px solid ${T.inputBorder}`,
    borderRadius: T.radius.input, color: T.text, fontSize: '0.95rem',
    transition: 'border-color 0.2s',
  },
  '& .react-tel-input .form-control:focus': {
    background: 'rgba(85,37,131,0.05)',
    border: `1.5px solid ${T.inputFocus}`,
    boxShadow: 'none', outline: 'none',
  },
  '& .react-tel-input .flag-dropdown': {
    background: 'transparent',
    border: `1px solid ${T.inputBorder}`,
    borderRight: 'none', borderRadius: `${T.radius.input} 0 0 ${T.radius.input}`,
  },
  '& .react-tel-input .flag-dropdown.open': {
    background: 'rgba(85,37,131,0.04)', borderRadius: `${T.radius.input} 0 0 ${T.radius.input}`,
  },
  '& .react-tel-input .selected-flag': {
    borderRadius: '9px 0 0 9px', background: 'transparent !important',
  },
  '& .react-tel-input .selected-flag:hover': { background: 'rgba(85,37,131,0.04) !important' },
  '& .react-tel-input .selected-flag .arrow': { borderTopColor: 'rgba(85,37,131,0.4)' },
  '& .react-tel-input .selected-flag .arrow.up': { borderBottomColor: 'rgba(85,37,131,0.4)' },
  '& .react-tel-input .country-list .flag': { display: 'none' },
  '& .react-tel-input .country-list': {
    background: '#f5efe8',
    border: '1px solid rgba(85,37,131,0.12)',
    borderRadius: '14px',
    boxShadow: '0 12px 40px rgba(85,37,131,0.1)',
    color: T.text, maxHeight: '260px',
    width: 'min(280px, 90vw)',
    margin: '6px 0 0 0', padding: '4px 0',
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(85,37,131,0.15) transparent',
  },
  '& .react-tel-input .country-list::-webkit-scrollbar': { width: '3px' },
  '& .react-tel-input .country-list::-webkit-scrollbar-thumb': { background: 'rgba(85,37,131,0.15)', borderRadius: '2px' },
  '& .react-tel-input .country-list .country': { padding: '10px 14px', transition: 'background 0.1s' },
  '& .react-tel-input .country-list .country:hover': { background: 'rgba(85,37,131,0.06)' },
  '& .react-tel-input .country-list .country.highlight': { background: 'rgba(85,37,131,0.08)' },
  '& .react-tel-input .country-list .country-name': { marginLeft: 0, color: 'rgba(0,0,0,0.75)', fontSize: '0.85rem' },
  '& .react-tel-input .country-list .dial-code': { color: 'rgba(0,0,0,0.3)', fontSize: '0.8rem' },
  '& .react-tel-input .country-list .divider': { borderColor: 'rgba(85,37,131,0.08)', margin: '4px 14px' },
  '& .react-tel-input .country-list .search': {
    padding: '10px 12px 6px', background: '#f5efe8',
    borderBottom: '1px solid rgba(85,37,131,0.06)',
    position: 'sticky', top: 0, zIndex: 1,
  },
  '& .react-tel-input .country-list .search-box': {
    width: '100%', boxSizing: 'border-box', margin: 0,
    background: 'rgba(85,37,131,0.04)',
    border: '1px solid rgba(85,37,131,0.12)',
    borderRadius: '8px', color: T.text,
    fontSize: '0.84rem', padding: '8px 12px', outline: 'none',
  },
  '& .react-tel-input .country-list .search-box:focus': { border: '1px solid rgba(85,37,131,0.35)' },
};

const Register = () => {
  const navigate = useNavigate();
  const { register, googleLogin, verifyEmail, resendVerificationEmail } = useAuth();
  const [googleLoading, setGoogleLoading] = useState(false);

  const googleOAuth = () => {
    setGoogleLoading(true);
    try {
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '854957052556-5n2rr4mh45uujceh8a60v837knoni7vk.apps.googleusercontent.com';
      if (!window.google?.accounts?.id) {
        setError('Google Sign-In non chargé. Rechargez la page.');
        setGoogleLoading(false);
        return;
      }
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response) => {
          try {
            await googleLogin({ credential: response.credential });
            navigate('/dashboard');
          } catch (err) {
            setError(err.response?.data?.message || 'Erreur connexion Google');
          }
          setGoogleLoading(false);
        },
      });
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          const tokenClient = window.google.accounts.oauth2.initTokenClient({
            client_id: clientId,
            scope: 'email profile',
            callback: async (tokenResponse) => {
              if (tokenResponse.access_token) {
                try {
                  await googleLogin({ access_token: tokenResponse.access_token });
                  navigate('/dashboard');
                } catch (err) {
                  setError(err.response?.data?.message || 'Erreur connexion Google');
                }
              }
              setGoogleLoading(false);
            },
          });
          tokenClient.requestAccessToken();
        }
      });
    } catch {
      setError('Erreur connexion Google');
      setGoogleLoading(false);
    }
  };

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    email: '', password: '', confirmPassword: '',
    phone: '+262', first_name: '', last_name: '', user_type: 'client',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [verificationToken, setVerificationToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    if (error) setError('');
  };

  const passwordStrength = useMemo(() => {
    const pwd = formData.password;
    if (!pwd) return { score: 0, label: '', color: 'inherit' };
    let score = 0;
    if (pwd.length >= 6) score++;
    if (pwd.length >= 10) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    if (score <= 1) return { score: 20, label: 'Très faible', color: '#e74c3c' };
    if (score === 2) return { score: 40, label: 'Faible', color: '#e67e22' };
    if (score === 3) return { score: 60, label: 'Moyen', color: '#d4a017' };
    if (score === 4) return { score: 80, label: 'Fort', color: T.purple };
    return { score: 100, label: 'Excellent', color: T.purpleDark };
  }, [formData.password]);

  const passwordsMatch = formData.confirmPassword && formData.password === formData.confirmPassword;

  const validateStep = () => {
    if (activeStep === 0) {
      if (!formData.first_name.trim() || !formData.last_name.trim()) {
        setError('Veuillez remplir votre prénom et votre nom'); return false;
      }
    }
    if (activeStep === 1) {
      if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        setError('Adresse email invalide'); return false;
      }
      if (!formData.phone || formData.phone.length < 8) {
        setError('Numéro de téléphone incomplet'); return false;
      }
    }
    setError(''); return true;
  };

  const handleNext = () => { if (validateStep()) setActiveStep(p => p + 1); };
  const handleBack = () => { setError(''); setActiveStep(p => p - 1); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) { setError('Les mots de passe ne correspondent pas'); return; }
    if (formData.password.length < 6) { setError('Minimum 6 caractères'); return; }
    setLoading(true);
    try {
      const { confirmPassword, ...data } = formData;
      const result = await register(data);
      if (result.requiresEmailVerification) {
        setVerificationToken(result.verificationToken);
        setActiveStep(3);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      if (err.response?.data?.passwordLeaked) {
        setError('Ce mot de passe a été détecté dans une fuite de données. Choisissez-en un autre.');
      } else {
        setError(err.response?.data?.message || "Erreur lors de l'inscription");
      }
    } finally { setLoading(false); }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setOtpLoading(true); setError('');
    try {
      await verifyEmail(verificationToken, otpCode);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Code incorrect ou expiré');
    } finally { setOtpLoading(false); }
  };

  const handleResend = async () => {
    setResending(true); setResent(false);
    try {
      await resendVerificationEmail(verificationToken);
      setResent(true);
      setTimeout(() => setResent(false), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du renvoi');
    } finally { setResending(false); }
  };

  const handlePhoneChange = (value) => {
    setFormData(prev => ({ ...prev, phone: '+' + value }));
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: T.bg }}>

      {/* ── Left panel ── */}
      <Box sx={{
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column', justifyContent: 'space-between',
        width: '42%', minHeight: '100vh',
        background: T.purple,
        p: 6, position: 'relative', overflow: 'hidden',
      }}>
        {/* Single accent — asymmetric, not multiple blobs */}
        <Box sx={{
          position: 'absolute', bottom: -100, right: -60,
          width: 380, height: 380, borderRadius: '50%',
          background: 'rgba(253,185,39,0.1)',
          pointerEvents: 'none',
        }} />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', letterSpacing: '0.15em', color: 'white' }}>
            USAFIRI
          </Typography>
        </Box>

        <Box sx={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 4 }}>
          <Typography sx={{
            fontWeight: 800, fontSize: '2.6rem', lineHeight: 1.1,
            color: 'white', mb: 1.5, letterSpacing: '-0.03em',
          }}>
            Tout Mayotte
            <Box component="span" sx={{ display: 'block', color: 'rgba(255,255,255,0.35)', fontSize: '2rem', mt: 0.5 }}>
              dans votre poche.
            </Box>
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.9rem', lineHeight: 1.7, maxWidth: 320, mb: 5 }}>
            Restaurants, courses, VTC, covoiturage a Mayotte.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {PERKS.map((p, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: '10px', flexShrink: 0,
                  background: 'rgba(253,185,39,0.12)', border: '1px solid rgba(253,185,39,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <p.IconComp sx={{ fontSize: 20, color: T.gold }} />
                </Box>
                <Box>
                  <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', mb: 0.2 }}>{p.title}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.78rem', lineHeight: 1.5 }}>{p.desc}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem' }}>Déjà un compte ?</Typography>
          <Link to="/login" style={{ color: T.gold, fontWeight: 700, fontSize: '0.82rem', textDecoration: 'none' }}>Se connecter</Link>
        </Box>
      </Box>

      {/* ── Right panel ── */}
      <Box sx={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: '100vh', overflowY: 'auto',
        p: { xs: 3, sm: 4, md: 6 },
      }}>
        <Box sx={{ width: '100%', maxWidth: 460 }}>

          {/* Mobile header */}
          <Box sx={{ display: { xs: 'flex', lg: 'none' }, justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', letterSpacing: '0.15em', color: T.purple }}>USAFIRI</Typography>
            <Link to="/login" style={{ color: T.muted, fontSize: '0.82rem', textDecoration: 'none' }}>Se connecter</Link>
          </Box>

          {/* Title */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.5rem', sm: '1.75rem' }, color: T.text, lineHeight: 1.2, mb: 0.5, letterSpacing: '-0.02em' }}>
              {activeStep === 0 && <><span>Bienvenue </span><WavingHandIcon sx={{ fontSize: 20, verticalAlign: 'text-bottom', color: T.gold }} /></>}
              {activeStep === 1 && 'Vos coordonnées'}
              {activeStep === 2 && 'Sécurisez votre compte'}
              {activeStep === 3 && 'Vérifiez votre email'}
            </Typography>
            <Typography sx={{ color: T.muted, fontSize: '0.86rem' }}>
              {activeStep === 0 && "Créez votre compte client gratuitement"}
              {activeStep === 1 && "Email et téléphone pour vous contacter"}
              {activeStep === 2 && "Choisissez un mot de passe solide"}
              {activeStep === 3 && `Un code a été envoyé à ${formData.email}`}
            </Typography>
          </Box>

          {/* Stepper — minimal line style, no numbered circles */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 4 }}>
            {STEPS.map((label, i) => (
              <React.Fragment key={i}>
                <Box sx={{
                  flex: 1, height: 3, borderRadius: 2,
                  background: i <= activeStep ? T.purple : 'rgba(0,0,0,0.08)',
                  transition: 'background 0.3s ease',
                }} />
              </React.Fragment>
            ))}
          </Box>

          {/* Step label */}
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: T.muted, letterSpacing: '0.04em', mb: 3 }}>
            Étape {activeStep + 1} sur {STEPS.length} — {STEPS[activeStep]}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: T.radius.alert, background: 'rgba(231,76,60,0.06)', border: '1px solid rgba(231,76,60,0.15)', '& .MuiAlert-icon': { color: '#e74c3c' } }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>

            {/* Step 1 */}
            {activeStep === 0 && (
              <Box>
                <Box sx={{ mb: 3 }}>
                  <Button
                    fullWidth onClick={() => googleOAuth()} disabled={googleLoading}
                    sx={{
                      py: 1.4, px: 2, borderRadius: T.radius.btn, background: 'white',
                      border: '1px solid rgba(0,0,0,0.13)', color: '#333',
                      fontWeight: 600, fontSize: '0.9rem', textTransform: 'none',
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      boxShadow: 'none',
                      transition: 'all 0.15s ease',
                      '&:hover': { background: '#fafafa', borderColor: 'rgba(0,0,0,0.25)', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
                      '&:active': { background: '#f5f5f5' },
                      '&:disabled': { opacity: 0.5 },
                    }}
                  >
                    {googleLoading ? <CircularProgress size={18} sx={{ color: '#4285F4' }} /> : <GoogleLogo />}
                    <span>S'inscrire avec Google</span>
                  </Button>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Divider sx={{ flex: 1, borderColor: 'rgba(0,0,0,0.08)' }} />
                  <Typography sx={{ px: 2, color: 'rgba(0,0,0,0.28)', fontSize: '0.73rem', fontWeight: 500 }}>ou</Typography>
                  <Divider sx={{ flex: 1, borderColor: 'rgba(0,0,0,0.08)' }} />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={labelSx}>Prénom</Typography>
                    <TextField fullWidth name="first_name" placeholder="Jean" value={formData.first_name} onChange={handleChange} autoFocus
                      InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutlineIcon sx={{ color: 'rgba(85,37,131,0.3)', fontSize: 18 }} /></InputAdornment> }}
                      sx={inputSx} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={labelSx}>Nom</Typography>
                    <TextField fullWidth name="last_name" placeholder="Dupont" value={formData.last_name} onChange={handleChange}
                      InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutlineIcon sx={{ color: 'rgba(85,37,131,0.3)', fontSize: 18 }} /></InputAdornment> }}
                      sx={inputSx} />
                  </Box>
                </Box>

                <Box sx={{ mb: 3.5, p: 2.5, borderRadius: '14px', background: 'rgba(85,37,131,0.04)', border: '1px solid rgba(85,37,131,0.1)' }}>
                  <Typography sx={{ color: T.muted, fontSize: '0.82rem', mb: 1.5, fontWeight: 500 }}>Vous êtes un professionnel ?</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                    {PARTNER_TYPES.map(({ IconComp, label }) => (
                      <Chip key={label} icon={<IconComp sx={{ fontSize: '15px !important', color: 'rgba(85,37,131,0.6) !important' }} />} label={label} size="small"
                        sx={{ bgcolor: 'rgba(85,37,131,0.07)', color: 'rgba(85,37,131,0.75)', border: '1px solid rgba(85,37,131,0.15)', fontSize: '0.76rem', fontWeight: 500, borderRadius: '8px' }} />
                    ))}
                  </Box>
                  <Link to="/partner/register" style={{ color: T.purple, fontWeight: 700, fontSize: '0.82rem', textDecoration: 'none' }}>
                    Créer un compte partenaire
                  </Link>
                </Box>

                <Button fullWidth variant="contained" onClick={handleNext} endIcon={<ArrowForwardIcon sx={{ fontSize: '18px !important' }} />} sx={btnPrimary}>
                  Continuer
                </Button>
              </Box>
            )}

            {/* Step 2 */}
            {activeStep === 1 && (
              <Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" sx={labelSx}>Adresse email</Typography>
                  <TextField fullWidth name="email" type="email" placeholder="jean@email.com"
                    value={formData.email} onChange={handleChange} autoFocus
                    InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlinedIcon sx={{ color: 'rgba(85,37,131,0.3)', fontSize: 18 }} /></InputAdornment> }}
                    sx={inputSx} />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={labelSx}>Téléphone</Typography>
                  <Box sx={phoneBoxSx}>
                    <PhoneInput
                      country="yt"
                      preferredCountries={['yt', 're', 'fr', 'km', 'mg', 'cd', 'cm', 'sn', 'ci', 'dj', 'mu']}
                      localization={{
                        yt: 'Mayotte', re: 'La Réunion', fr: 'France', km: 'Comores',
                        mg: 'Madagascar', cd: 'Congo (RDC)', cm: 'Cameroun', sn: 'Sénégal',
                        ci: "Côte d'Ivoire", dj: 'Djibouti', mu: 'Maurice',
                      }}
                      value={formData.phone.replace('+', '')}
                      onChange={handlePhoneChange}
                      enableSearch disableSearchIcon
                      searchPlaceholder="Rechercher un pays..."
                      countryCodeEditable={false}
                      inputProps={{ name: 'phone', required: true, autoComplete: 'tel' }}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
                  <Button variant="outlined" onClick={handleBack} startIcon={<ArrowBackIcon />} sx={btnSecondary}>Retour</Button>
                  <Button fullWidth variant="contained" onClick={handleNext} endIcon={<ArrowForwardIcon sx={{ fontSize: '18px !important' }} />} sx={btnPrimary}>Continuer</Button>
                </Box>
              </Box>
            )}

            {/* Step 3 */}
            {activeStep === 2 && (
              <Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" sx={labelSx}>Mot de passe</Typography>
                  <TextField fullWidth name="password" type={showPassword ? 'text' : 'password'}
                    placeholder="Minimum 6 caractères" value={formData.password} onChange={handleChange} autoFocus
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ color: 'rgba(85,37,131,0.3)', fontSize: 18 }} /></InputAdornment>,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} sx={{ color: 'rgba(85,37,131,0.35)', '&:hover': { color: T.purple } }}>
                            {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={inputSx} />
                  {formData.password && (
                    <Box sx={{ mt: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.6 }}>
                        <Typography sx={{ color: 'rgba(0,0,0,0.3)', fontSize: '0.68rem' }}>Force du mot de passe</Typography>
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: passwordStrength.color }}>{passwordStrength.label}</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={passwordStrength.score}
                        sx={{ height: 3, borderRadius: 2, bgcolor: 'rgba(85,37,131,0.06)', '& .MuiLinearProgress-bar': { bgcolor: passwordStrength.color, borderRadius: 2, transition: 'all 0.3s' } }} />
                    </Box>
                  )}
                </Box>

                <Box sx={{ mb: 3.5 }}>
                  <Typography variant="caption" sx={labelSx}>Confirmer le mot de passe</Typography>
                  <TextField fullWidth name="confirmPassword" type={showConfirm ? 'text' : 'password'}
                    placeholder="Répétez votre mot de passe" value={formData.confirmPassword} onChange={handleChange}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ color: 'rgba(85,37,131,0.3)', fontSize: 18 }} /></InputAdornment>,
                      endAdornment: (
                        <InputAdornment position="end">
                          {formData.confirmPassword && (
                            passwordsMatch
                              ? <CheckCircleOutlineIcon sx={{ color: T.purple, fontSize: 18, mr: 0.5 }} />
                              : <Typography sx={{ color: '#e74c3c', fontSize: '0.75rem', mr: 0.5, fontWeight: 700 }}>!=</Typography>
                          )}
                          <IconButton onClick={() => setShowConfirm(!showConfirm)} sx={{ color: 'rgba(85,37,131,0.35)', '&:hover': { color: T.purple } }}>
                            {showConfirm ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      ...inputSx,
                      '& .MuiOutlinedInput-root fieldset': {
                        borderColor: formData.confirmPassword
                          ? passwordsMatch ? 'rgba(85,37,131,0.35)' : 'rgba(231,76,60,0.35)'
                          : T.inputBorder,
                      },
                    }} />
                </Box>

                <Typography sx={{ color: 'rgba(0,0,0,0.3)', fontSize: '0.74rem', lineHeight: 1.7, mb: 3 }}>
                  En créant un compte, vous acceptez nos{' '}
                  <Link to="/terms" style={{ color: T.purple, textDecoration: 'none', fontWeight: 600 }}>Conditions d'utilisation</Link>
                  {' '}et notre{' '}
                  <Link to="/privacy" style={{ color: T.purple, textDecoration: 'none', fontWeight: 600 }}>Politique de confidentialité</Link>.
                </Typography>

                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Button variant="outlined" onClick={handleBack} startIcon={<ArrowBackIcon />} sx={btnSecondary}>Retour</Button>
                  <Button type="submit" fullWidth variant="contained" disabled={loading} endIcon={!loading && <ArrowForwardIcon sx={{ fontSize: '18px !important' }} />} sx={btnPrimary}>
                    {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Créer mon compte'}
                  </Button>
                </Box>
              </Box>
            )}

            {/* Step 4 — Email verification */}
            {activeStep === 3 && (
              <Box>
                <Box sx={{ textAlign: 'center', mb: 3.5 }}>
                  <Box sx={{
                    width: 56, height: 56, borderRadius: '16px', mx: 'auto', mb: 2,
                    background: 'rgba(85,37,131,0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <MarkEmailReadIcon sx={{ fontSize: 28, color: T.purple }} />
                  </Box>
                  <Typography sx={{ color: T.muted, fontSize: '0.88rem', lineHeight: 1.7 }}>
                    Entrez le code à 6 chiffres envoyé à<br />
                    <Box component="span" sx={{ color: T.purple, fontWeight: 700 }}>{formData.email}</Box>
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography sx={labelSx}>Code de verification</Typography>
                  <TextField
                    fullWidth autoFocus
                    value={otpCode}
                    onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                    sx={{
                      ...inputSx,
                      '& input': { color: T.text, py: 1.6, letterSpacing: '0.4em', fontSize: '1.5rem', fontWeight: 700, textAlign: 'center', '&::placeholder': { color: 'rgba(0,0,0,0.12)', letterSpacing: '0.2em', fontSize: '1.1rem' } },
                    }}
                  />
                </Box>

                <Button
                  fullWidth variant="contained"
                  onClick={handleVerifyEmail}
                  disabled={otpLoading || otpCode.length !== 6}
                  endIcon={!otpLoading && <ArrowForwardIcon sx={{ fontSize: '18px !important' }} />}
                  sx={{ ...btnPrimary, mb: 3 }}
                >
                  {otpLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Activer mon compte'}
                </Button>

                <Box sx={{ textAlign: 'center' }}>
                  {resent ? (
                    <Typography sx={{ color: '#2e7d32', fontWeight: 600, fontSize: '0.85rem' }}><CheckIcon sx={{ fontSize: 14, verticalAlign: 'text-bottom', mr: 0.3 }} />Code renvoye</Typography>
                  ) : (
                    <Typography sx={{ color: 'rgba(0,0,0,0.4)', fontSize: '0.85rem' }}>
                      Pas recu ?{' '}
                      <Box component="span" onClick={!resending ? handleResend : undefined}
                        sx={{ color: T.purple, fontWeight: 700, cursor: resending ? 'default' : 'pointer', textDecoration: 'underline', opacity: resending ? 0.5 : 1 }}>
                        {resending ? 'Envoi...' : 'Renvoyer'}
                      </Box>
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            <Box sx={{ display: { xs: 'block', lg: 'none' }, textAlign: 'center', mt: 4 }}>
              <Typography sx={{ color: 'rgba(0,0,0,0.35)', fontSize: '0.82rem' }}>
                Deja un compte ?{' '}
                <Link to="/login" style={{ color: T.purple, fontWeight: 700, textDecoration: 'none' }}>Se connecter</Link>
              </Typography>
            </Box>

          </form>
        </Box>
      </Box>
    </Box>
  );
};

export default Register;
